import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import { BookTimelineView } from "../components/timeline/BookTimelineView";
import { TimelineSpinner } from "../components/timeline/TimelineSpinner";
import { bookFromSlugs, chapterFromSlug, pageTitle } from "../config/site";
import { formatChapterId, parseChapterRoute } from "../lib/timeline/book/ChapterId";
import { applyUrlOptions } from "../lib/timeline/view/UrlOptions/apply";
import { parseUrlOptions } from "../lib/timeline/view/UrlOptions/options";
import { NotFoundPage } from "./NotFoundPage";
import { loadBookTimeline, type BookTimelineModel } from "./loadBookTimeline";

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string; missing: boolean }
  | ({ status: "ready" } & BookTimelineModel);

export function BookTimelinePage() {
  const { seriesSlug, bookSlug, chapterSlug } = useParams();
  const { search } = useLocation();
  const book = bookFromSlugs(seriesSlug, bookSlug);
  const chapterNumber = chapterSlug === undefined ? null : chapterFromSlug(chapterSlug);
  const badAddress = book === undefined || (chapterSlug !== undefined && chapterNumber === null);
  const chapterId = chapterNumber === null ? undefined : formatChapterId(chapterNumber);
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    if (badAddress) {
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    applyUrlOptions(book.id, parseUrlOptions(search), chapterId);
    void loadBookTimeline(book.id, chapterId)
      .then((model) => {
        if (!cancelled) {
          setState({ status: "ready", ...model });
        }
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        if (!cancelled) {
          setState({ status: "error", message, missing: chapterId !== undefined });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [badAddress, book?.id, chapterId, search]);

  useEffect(() => {
    if (state.status === "ready") {
      document.title = pageTitle(state.title);
    }
  }, [state]);

  if (badAddress) {
    return <NotFoundPage />;
  }

  if (state.status === "loading") {
    return (
      <TimelineSpinner
        label="Loading timeline…"
        page
        phrasesBookId={book.id}
        chapterId={chapterId === undefined ? undefined : parseChapterRoute(chapterId)}
      />
    );
  }
  if (state.status === "error") {
    if (state.missing) {
      return <NotFoundPage />;
    }
    return (
      <div className="timeline-status">
        <p>{state.message}</p>
        <Link to="/">Back home</Link>
      </div>
    );
  }

  return (
    <BookTimelineView
      chapters={state.chapters}
      spans={state.spans}
      initialSpanId={state.initialSpanId}
      loadUniverse={state.loadUniverse}
      initialUniverse={state.initialUniverse}
    />
  );
}
